import type { PlotChapter } from "@/types/content";

export const plotChapters: PlotChapter[] = [
  {
    id: "chapter-1",
    index: "01",
    title: "序章：一局没有赢家的牌局",
    lead: "影片从一个封闭的室内空间开始，牌桌、烟雾与客套话构成了最初的世界秩序。",
    image:
      "https://readdy.ai/api/search-image?query=Ornate%201940s%20Chinese%20mansion%20interior%20with%20a%20mahjong%20table%20in%20the%20center%2C%20four%20elegant%20chairs%2C%20warm%20lamp%20glow%2C%20crimson%20drapes%2C%20hazy%20smoke%2C%20ivory%20tiles%20scattered%20on%20green%20felt%2C%20cinematic%20wide%20composition%2C%20muted%20gold%20and%20deep%20red%20palette%2C%20no%20text&width=900&height=600&seq=sejie-plot-mahjong-01&orientation=landscape",
    paragraphs: [
      "开场并不急着交代人物关系，而是先把观众放进一个讲究规矩的空间：谁坐哪里、谁先说话、谁负责倒茶，都按既定秩序运行。规矩越完整，越显得底下有东西在动。",
      "这一段的镜头始终保持在门外半步的位置，既能看清全局，又不便插话。观众和局中人一样，只能从语气与停顿里猜测真实意图。",
    ],
  },
  {
    id: "chapter-2",
    index: "02",
    title: "身份的诞生：从学生到“麦太太”",
    lead: "一个年轻人在集体热情的推动下，接下一个自己并不完全理解的角色。",
    paragraphs: [
      "学生阶段的段落明亮、嘈杂、略带莽撞，与后半段的昏暗形成强烈对照。那种把理想当作游戏的天真，正是后来所有代价的来源。",
      "当她第一次穿上旗袍、被要求改变走路方式时，镜头刻意停留了很久。表演从这里开始变成一种自我改造：她需要先成为另一个人，才能完成被交付的任务。",
      "值得注意的是，周围人给她的只有鼓励，没有人告诉她结束之后该怎么回来。身份的裂缝在此时就已经埋下。",
    ],
  },
  {
    id: "chapter-3",
    index: "03",
    title: "靠近与试探：所有对话都有第二层",
    lead: "重逢之后，两个人之间建立起一种既亲密又危险的默契。",
    paragraphs: [
      "这一段的节奏明显放缓，大量时间被交给沉默与琐碎动作：整理袖口、递火、看窗外的雨。真正的信息藏在谁先打破沉默里。",
      "台词几乎都在试探边界：一句关心可以是情感，也可以是审讯；一次让步既可能是信任，也可能是布局。观众被迫在两种解释之间反复摇摆。",
      "麻将桌边的闲聊承担了同样的功能。太太们谈论首饰与衣料，实际上在交换情报；而真正要紧的事，往往由一句轻描淡写的玩笑带过。",
    ],
  },
  {
    id: "chapter-4",
    index: "04",
    title: "转折：珠宝店里的那道光",
    lead: "整部影片最关键的一次选择，发生在一个过于明亮的空间里。",
    image:
      "https://readdy.ai/api/search-image?query=Interior%20of%20a%201940s%20jewelry%20shop%20with%20glass%20display%20cabinets%2C%20soft%20cool%20daylight%20through%20blinds%2C%20small%20diamond%20ring%20on%20crimson%20velvet%20cushion%2C%20brass%20fixtures%2C%20elegant%20restrained%20composition%2C%20cinematic%20still%2C%20muted%20palette%20with%20warm%20gold%20highlights%2C%20no%20text&width=900&height=600&seq=sejie-plot-jewelry-04&orientation=landscape",
    paragraphs: [
      "珠宝店是全片少见的冷白光空间，光线把每个人的表情都照得过分清楚，也让隐藏的犹豫无处可躲。",
      "戒指被推到台面上的瞬间，关系从私下的默契变成了可以被见证的事实。物件本身没有情感，但它让情感无法再被否认。",
      "那句低声说出的话，既像请求也像放手。完整版本在这里留了足够长的静默，让观众自己听见心里那声“糟了”。",
    ],
  },
  {
    id: "chapter-5",
    index: "05",
    title: "逃离与封锁：街道成为牢笼",
    lead: "当决定已经做出，空间开始收紧，所有出口都通向同一个方向。",
    image:
      "https://readdy.ai/api/search-image?query=Narrow%20rain-soaked%20alley%20in%20old%20Shanghai%20at%20night%2C%20closed%20iron%20gates%2C%20flickering%20street%20lamp%2C%20wet%20cobblestones%20with%20crimson%20reflections%2C%20distant%20figures%20in%20dark%20coats%2C%20heavy%20rain%20streaks%2C%20tense%20cinematic%20atmosphere%2C%20desaturated%20moody%20color%20grading%2C%20no%20text&width=900&height=600&seq=sejie-plot-street-05&orientation=landscape",
    paragraphs: [
      "黄包车穿过街道的段落几乎没有对白，只有车轮碾过积水的声音。镜头不断被屋檐、栅栏与人群切断，观众能感觉到空间正在缩小。",
      "封锁线是一道明确的界线：线内的人还在等待，线外的人已经无法回头。此处不需要任何解释，地理本身已经完成了叙事。",
    ],
  },
  {
    id: "chapter-6",
    index: "06",
    title: "终章：留下的那点光",
    lead: "结尾拒绝给出答案，只给出一段空荡的空间与逐渐远去的声音。",
    paragraphs: [
      "配乐提前退场，脚步声、雨声与远处车响成为主角。听觉上的空，让时间变得异常漫长，也让观众不得不面对自己刚刚做出的判断。",
      "镜头最后停在一处没有人的地方。这不是省略，而是把解释权交还给观众：有些结局无法被完整讲述，只能被记住。",
      "重看时你会发现，前面的每一次沉默都在为这个结尾做准备。完整版本的价值，也许正在于它愿意把这些沉默保留下来。",
    ],
  },
];